/**
 * The medicine a visitor came in with travels to the next page in the URL
 * fragment (`/upload-prescription#medication=Metformin&src=drug-metformin`),
 * not in the query string.
 *
 * Why (2026-10-16): Google Analytics records every page's URL, query string
 * included, and in a single-page app it records each client-side navigation
 * too. A query-string hand-off therefore told Google that this browser was
 * asking pharmacies to quote on Metformin 500 mg, and after a request was sent
 * that it had just submitted one (/prescription-live?medication=...). That is
 * the kind of sharing the FTC acted on against GoodRx. GA4 leaves the fragment
 * out of page_location, browsers never send it to any server, and the PostHog
 * set-up strips it (src/components/analytics/PostHogProvider.tsx).
 *
 * Old links, emails and agent answers still use the query string, so the
 * pages read both, and move a query-string hand-off into the fragment as soon
 * as they load, before the analytics scripts start.
 *
 * Plain module (no 'use client') so server components can build links with it.
 */

/** Keys that can name a medicine, a dose, a condition or the page that sent the visitor. */
export const HANDOFF_KEYS = ['medication', 'drug', 'q', 'dosage', 'supply', 'src', 'pet', 'from_agent'] as const;

type HandoffValues = Partial<Record<(typeof HANDOFF_KEYS)[number], string | undefined | null | false>>;

function fragment(values: HandoffValues): string {
  const p = new URLSearchParams();
  for (const key of HANDOFF_KEYS) {
    const v = values[key];
    if (v) p.set(key, v);
  }
  const s = p.toString();
  return s ? `#${s}` : '';
}

/** Link to the upload page carrying the hand-off in the fragment. */
export function uploadHref(values: HandoffValues = {}): string {
  return `/upload-prescription${fragment(values)}`;
}

/** Link to the medication lookup with a search term, in the fragment. */
export function lookupHref(term: string): string {
  return `/medication-lookup${fragment({ q: term })}`;
}

/** Parse the fragment as key=value pairs (empty when it is a plain anchor). */
export function readFragment(hash: string): URLSearchParams {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  return raw.includes('=') ? new URLSearchParams(raw) : new URLSearchParams();
}

/**
 * Hand-off values from the fragment, with the query string as the fallback
 * for links that predate the fragment.
 */
export function readHandoff(loc: { search: string; hash: string }): URLSearchParams {
  const out = new URLSearchParams();
  const query = new URLSearchParams(loc.search);
  const frag = readFragment(loc.hash);
  for (const key of HANDOFF_KEYS) {
    const v = frag.get(key) ?? query.get(key);
    if (v) out.set(key, v);
  }
  return out;
}

/**
 * If the query string carries hand-off keys, return the same URL with those
 * keys moved into the fragment (other parameters, such as utm_source, stay in
 * the query string for attribution). Returns null when there is nothing to move.
 */
export function moveHandoffToFragment(loc: { pathname: string; search: string; hash: string }): string | null {
  const query = new URLSearchParams(loc.search);
  if (!HANDOFF_KEYS.some((k) => query.has(k))) return null;
  const values = readHandoff(loc);
  for (const key of HANDOFF_KEYS) query.delete(key);
  const rest = query.toString();
  const frag = new URLSearchParams();
  for (const [k, v] of values) frag.set(k, v);
  const f = frag.toString();
  return `${loc.pathname}${rest ? `?${rest}` : ''}${f ? `#${f}` : ''}`;
}
